"use client";

import { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Bed,
  Bath,
  Home,
  Ruler,
  Gauge,
  Calendar,
  Settings,
  CheckCircle,
  Tag,
  Expand,
  Users,
  CalendarCheck,
  Clock,
  Shirt,
  Zap,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";

// Dynamic configuration for categories and their fields
const CATEGORY_CONFIG = {
  house: {
    id: 1,
    name: "House",
    fields: [
      {
        key: "bedrooms",
        label: "Bedrooms",
        icon: Bed,
        type: "number",
        suffix: "",
      },
      {
        key: "bathrooms",
        label: "Bathrooms",
        icon: Bath,
        type: "number",
        suffix: "",
      },
      {
        key: "squareFoot",
        label: "Size",
        icon: Ruler,
        type: "number",
        suffix: " sq ft",
      },
      {
        key: "propertyType",
        label: "Type",
        icon: Home,
        type: "text",
        transform: "capitalize",
      },
      {
        key: "condition",
        label: "Condition",
        icon: CheckCircle,
        type: "rating",
        suffix: "/10",
      },
    ],
    amenitiesEnabled: true,
    preownedEnabled: true,
  },
  sublease: {
    id: 2,
    name: "Sublease",
    fields: [
      {
        key: "bedrooms",
        label: "Bedrooms",
        icon: Bed,
        type: "number",
        suffix: "",
      },
      {
        key: "bathrooms",
        label: "Bathrooms",
        icon: Bath,
        type: "number",
        suffix: "",
      },
      {
        key: "squareFoot",
        label: "Size",
        icon: Ruler,
        type: "number",
        suffix: " sq ft",
      },
      {
        key: "propertyType",
        label: "Type",
        icon: Home,
        type: "text",
        transform: "capitalize",
      },
      {
        key: "condition",
        label: "Condition",
        icon: CheckCircle,
        type: "rating",
        suffix: "/10",
      },
      {
        key: "totalLeaseLength",
        label: "Total Lease",
        icon: Calendar,
        type: "number",
        suffix: " months",
      },
      {
        key: "remainingLease",
        label: "Remaining",
        icon: Clock,
        type: "number",
        suffix: " months",
      },
      {
        key: "furnished",
        label: "Furnished",
        icon: Home,
        type: "text",
        transform: "capitalize",
      },
      {
        key: "isUtilities",
        label: "Utilities",
        icon: Zap,
        type: "boolean",
        trueText: "Included",
        falseText: "Not Included",
      },
    ],
    amenitiesEnabled: true,
    preownedEnabled: true,
  },
  auto: {
    id: 3,
    name: "Auto",
    fields: [
      {
        key: "condition",
        label: "Condition",
        icon: CheckCircle,
        type: "rating",
        suffix: "/10",
      },
      {
        key: "kilometers",
        label: "Kilometers",
        icon: Gauge,
        type: "number",
        suffix: " km",
        format: "comma",
      },
      {
        key: "engineType",
        label: "Engine",
        icon: Settings,
        type: "text",
        transform: "capitalize",
      },
      { key: "modelReleaseYear", label: "Year", icon: Calendar, type: "text" },
    ],
    amenitiesEnabled: false,
    preownedEnabled: true,
  },
  electronics: {
    id: 4,
    name: "Electronics",
    fields: [
      {
        key: "condition",
        label: "Condition",
        icon: CheckCircle,
        type: "rating",
        suffix: "/10",
      },
      { key: "modelReleaseYear", label: "Year", icon: Calendar, type: "text" },
      { key: "brand", label: "Brand", icon: Tag, type: "text" },
      { key: "model", label: "Model", icon: Tag, type: "text" },
    ],
    amenitiesEnabled: false,
    preownedEnabled: true,
  },
  appliances: {
    id: 5,
    name: "appliances",
    fields: [
      {
        key: "condition",
        label: "Condition",
        icon: CheckCircle,
        type: "rating",
        suffix: "/10",
      },
      { key: "modelReleaseYear", label: "Year", icon: Calendar, type: "text" },
      { key: "brand", label: "Brand", icon: Tag, type: "text" },
      { key: "model", label: "Model", icon: Tag, type: "text" },
    ],
    amenitiesEnabled: false,
    preownedEnabled: true,
  },
  apparels: {
    id: 6,
    name: "apparels",
    fields: [
      {
        key: "condition",
        label: "Condition",
        icon: CheckCircle,
        type: "rating",
        suffix: "/10",
      },
      {
        key: "apparelType",
        label: "Type",
        icon: Shirt,
        type: "text",
        transform: "capitalize",
      },
      {
        key: "size",
        label: "Size",
        icon: Expand,
        type: "text",
        transform: "uppercase",
      },
      { key: "brand", label: "Brand", icon: Tag, type: "text" },
    ],
    amenitiesEnabled: false,
    preownedEnabled: false,
  },
  roommate: {
    id: 7,
    name: "Roommate",
    fields: [
      {
        key: "genderPreference",
        label: "Gender Pref.",
        icon: Users,
        type: "text",
        transform: "capitalize",
      },
      { key: "ageRange", label: "Age Range", icon: Users, type: "text" },
      {
        key: "moveInDate",
        label: "Move In",
        icon: CalendarCheck,
        type: "date",
      },
      {
        key: "leaseDuration",
        label: "Lease Duration",
        icon: CalendarCheck,
        type: "text",
        transform: "capitalize",
      },
      {
        key: "lifestyle",
        label: "Lifestyle",
        icon: Home,
        type: "text",
        transform: "capitalize",
      },
    ],
    amenitiesEnabled: false,
    preownedEnabled: false,
  },
};

const LoadingSpinner = ({ size = 40 }) => (
  <div className="flex items-center justify-center">
    <div
      className="animate-spin rounded-full border-4 border-gray-200 border-t-[#7B189F]"
      style={{ width: size, height: size }}
    />
  </div>
);

// Dynamic amenities configuration
const AMENITIES_CONFIG = {
  parking: { label: "🚗 Parking", category: "general" },
  wifi: { label: "📶 Wi-Fi", category: "utilities" },
  "air-conditioning": { label: "❄️ Air Conditioning", category: "climate" },
  heating: { label: "🔥 Heating", category: "climate" },
  "pet-friendly": { label: "🐕 Pet-Friendly", category: "lifestyle" },
  "laundry-in-unit": { label: "👕 Laundry In-Unit", category: "utilities" },
  "washer-dryer-on-site": {
    label: "🔄 Washer/Dryer On-Site",
    category: "utilities",
  },
  "balcony-patio": { label: "🏠 Balcony/Patio", category: "features" },
  fireplace: { label: "🔥 Fireplace", category: "features" },
  "fully-equipped-kitchen": {
    label: "🍳 Fully Equipped Kitchen",
    category: "features",
  },
  gym: { label: "💪 Gym", category: "amenities" },
  "gated-community": { label: "🏘️ Gated Community", category: "security" },
  "security-system": { label: "🔒 Security System", category: "security" },
};

function ClassifiedDetailsView({ listing, category, onBack }) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    reason: false,
    description: false,
  });
  const [imageLoading, setImageLoading] = useState(true);
  const [thumbnailsLoading, setThumbnailsLoading] = useState({});

  // Scroll to top when component loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (!listing) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>No listing selected</p>
      </div>
    );
  }

  // Get category configuration
  const categoryKey = category?.toLowerCase() || "general";
  const categoryConfig = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.house;

  // Dynamic data parsing
  const parseListingData = () => {
    const result = {
      basic: {
        id: listing.id,
        title: listing.title,
        price: listing.price,
        description: listing.description,
        story:
          listing.story || listing.reasonForSelling || listing.reasonToSell,
        images: (() => {
          const coverPhoto = listing.coverPhoto;
          const otherImages = listing.images || [listing.image];

          // If we have a cover photo, make sure it's first
          if (coverPhoto) {
            const filteredImages = Array.isArray(otherImages)
              ? otherImages.filter((img) => img && img !== coverPhoto)
              : otherImages && otherImages !== coverPhoto
              ? [otherImages]
              : [];
            return [coverPhoto, ...filteredImages];
          }

          // Fallback to original logic if no cover photo
          return Array.isArray(otherImages) ? otherImages : [otherImages];
        })(),
      },
      specific: {},
      contact: {
        fullName: listing.fullName || listing.contactName,
        email: listing.email || listing.contactEmail,
        phone: listing.phoneNumber || listing.contactPhone,
        address: listing.addressLine1 || listing.address,
        city: listing.city,
        state: listing.state,
        zip: listing.zip,
      },
    };

    // Parse specific details from various possible locations
    const specificSources = [
      listing.specific_details,
      listing.specificDetails,
      listing.details,
      listing, // fallback to top level
    ];

    specificSources.forEach((source) => {
      if (!source) return;

      if (Array.isArray(source)) {
        source.forEach((item) => {
          if (typeof item === "object" && item !== null) {
            Object.assign(result.specific, item);
          }
        });
      } else if (typeof source === "object") {
        Object.assign(result.specific, source);
      }
    });

    return result;
  };

  const listingData = parseListingData();

  // Dynamic field rendering
  const renderField = (fieldConfig, value) => {
    if (!value && value !== 0 && value !== false) return null;

    const {
      label,
      icon: Icon,
      type,
      suffix = "",
      transform,
      format,
      trueText,
      falseText,
    } = fieldConfig;

    let displayValue = value;

    // Apply transformations
    switch (type) {
      case "number":
        if (format === "comma") {
          displayValue = Number(value).toLocaleString();
        }
        displayValue = `${displayValue}${suffix}`;
        break;
      case "rating":
        displayValue = `${value}${suffix}`;
        break;
      case "boolean":
        displayValue = value ? trueText || "Yes" : falseText || "No";
        break;
      case "date":
        displayValue = new Date(value).toLocaleDateString();
        break;
      case "text":
      default:
        if (transform === "capitalize") {
          displayValue =
            String(value).charAt(0).toUpperCase() +
            String(value).slice(1).replace(/[-_]/g, " ");
        } else if (transform === "uppercase") {
          displayValue = String(value).toUpperCase();
        }
        displayValue = `${displayValue}${suffix}`;
        break;
    }

    return (
      <div
        key={fieldConfig.key}
        className="flex-1 min-w-[100px] flex flex-col items-center"
      >
        <div className="w-10 h-10 mb-1 flex items-center justify-center text-gray-600">
          <Icon size={24} />
        </div>
        <div className="text-sm text-gray-500 font-afacad uppercase tracking-wide">
          {label}
        </div>
        <div className="text-sm font-medium text-gray-900 font-afacad text-center">
          {displayValue}
        </div>
      </div>
    );
  };

  // Dynamic property details rendering
  const renderPropertyDetails = () => {
    const fields = categoryConfig.fields
      .map((fieldConfig) => {
        const value = listingData.specific[fieldConfig.key];
        return value !== undefined && value !== null && value !== ""
          ? renderField(fieldConfig, value)
          : null;
      })
      .filter(Boolean);

    if (fields.length === 0) return null;

    return (
      <div className="border-t border-b border-gray-200 py-6 my-6">
        <div className="flex flex-wrap justify-between gap-y-4">{fields}</div>
      </div>
    );
  };

  // Dynamic amenities rendering
  const renderAmenities = () => {
    if (!categoryConfig.amenitiesEnabled) return null;

    let amenities = [];

    // Check for amenities in various formats
    const amenitySources = [
      listingData.specific.amenities,
      listingData.specific.features,
      listingData.specific.facilities,
    ];

    amenitySources.forEach((source) => {
      if (Array.isArray(source)) {
        amenities = [...amenities, ...source];
      } else if (typeof source === "string") {
        amenities = [...amenities, ...source.split(",").map((a) => a.trim())];
      }
    });

    // Check for individual amenity flags
    Object.keys(AMENITIES_CONFIG).forEach((amenityKey) => {
      const normalizedKey = amenityKey.replace(/[-_]/g, "");
      if (
        listingData.specific[amenityKey] === true ||
        listingData.specific[normalizedKey] === true ||
        listingData.specific[amenityKey] === "yes" ||
        listingData.specific[normalizedKey] === "yes"
      ) {
        amenities.push(amenityKey);
      }
    });

    // Remove duplicates and filter valid amenities
    amenities = [...new Set(amenities)].filter(
      (amenity) => amenity && amenity.trim()
    );

    if (amenities.length === 0) return null;

    return (
      <div className="mt-6  border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3 font-afacad uppercase tracking-wide">
          Amenities
        </h4>
        <div className="flex flex-wrap gap-2">
          {amenities.map((amenityId, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
            >
              {AMENITIES_CONFIG[amenityId]?.label || amenityId}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Dynamic preowned status
  const renderPreownedStatus = () => {
    if (!categoryConfig.preownedEnabled) return null;
    const rawIsPreowned = listingData.specific.isPreowned;
    if (rawIsPreowned === undefined || rawIsPreowned === null) return null;
    const isPreowned = rawIsPreowned === true || rawIsPreowned === "true";

    return (
      <div className="mb-4">
        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium font-afacad bg-black text-white">
          {isPreowned ? "Used" : "New"}
        </span>
      </div>
    );
  };

  // Dynamic collapsible section
  const renderCollapsibleSection = (key, title, content, icon = null) => {
    if (!content) return null;

    const isExpanded = expandedSections[key];

    return (
      <div className="mt-4 bg-gray-50 rounded-lg">
        <button
          className="flex items-center justify-between w-full p-4 text-left"
          onClick={() =>
            setExpandedSections((prev) => ({
              ...prev,
              [key]: !prev[key],
            }))
          }
        >
          <div className="flex items-center">
            {icon && <span className="text-gray-400 mr-2">{icon}</span>}
            <span className="font-medium">{title}</span>
          </div>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        {isExpanded && <div className="px-4 pb-4 text-gray-700">{content}</div>}
      </div>
    );
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return "$0";
    return `$${Number.parseInt(price).toLocaleString()}`;
  };

  // Prepare images
  const images = Array.isArray(listingData.basic.images)
    ? listingData.basic.images.filter(Boolean)
    : [listingData.basic.images].filter(Boolean);

  if (images.length === 0) {
    images.push("/placeholder.svg?height=600&width=800");
  }

  // Breadcrumb navigation
  const renderBreadcrumbs = () => (
    <div className="flex items-center text-sm text-gray-500 mb-4">
      <a href="/classifieds" className="hover:text-gray-700">
        Classifieds
      </a>
      <span className="mx-2">›</span>
      <a
        href={`/classifieds/categories/${categoryKey}`}
        className="hover:text-gray-700"
      >
        {categoryConfig.name}
      </a>
      <span className="mx-2">›</span>
      <span className="text-gray-700">{listingData.basic.title}</span>
    </div>
  );

  // Contact section
  const renderContactSection = () => {
    const { fullName, email, phone, address, city, state, zip } =
      listingData.contact;

    if (!fullName && !email && !phone) return null;

    return (
      <div className="mt-8">
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 font-afacad">
              Seller Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                {fullName && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2 font-afacad uppercase tracking-wide">
                      Seller Name
                    </h4>
                    <p className="text-gray-900 font-medium text-base font-afacad">
                      {fullName}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-900 font-afacad">{phone}</span>
                    </div>
                  )}
                  {email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-900 font-afacad break-all">
                        {email}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                {(address || city || state) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2 font-afacad uppercase tracking-wide">
                      Location
                    </h4>
                    <div className="text-gray-900 font-afacad space-y-1">
                      {address && <p>{address}</p>}
                      <p>{[city, state, zip].filter(Boolean).join(", ")}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact actions */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center sm:justify-between">
              <button className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-700 text-white font-medium hover:bg-purple-800 transition-colors rounded-lg font-afacad">
                <Phone className="h-4 w-4" />
                <span>Contact Seller</span>
              </button>

              <button className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors rounded-lg font-afacad">
                <MapPin className="h-4 w-4" />
                <span>View Location</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Breadcrumb navigation */}
      {renderBreadcrumbs()}

      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center text-gray-600 mb-6 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to listings
      </button>

      {/* Main content */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-12">
        {/* Left column - Images */}
        <div className="mb-8 lg:mb-0">
          {/* Main image */}
          <div className="relative w-full aspect-[4/3] mb-4 overflow-hidden rounded-lg bg-gray-100">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <LoadingSpinner size={48} />
              </div>
            )}
            <img
              src={
                images[selectedImage] || "/placeholder.svg?height=600&width=800"
              }
              alt={`${listingData.basic.title} - Image ${selectedImage + 1}`}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoading ? "opacity-0" : "opacity-100"
              }`}
              onLoad={() => setImageLoading(false)}
              onLoadStart={() => setImageLoading(true)}
            />
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="overflow-x-auto">
              <div className="flex space-x-3 pb-2">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`relative w-20 h-20 cursor-pointer rounded-md overflow-hidden border-2 transition-colors ${
                      selectedImage === index
                        ? "border-purple-600"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => {
                      setSelectedImage(index);
                      setImageLoading(true);
                    }}
                  >
                    {thumbnailsLoading[index] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <LoadingSpinner size={20} />
                      </div>
                    )}
                    <img
                      src={image || "/placeholder.svg?height=200&width=200"}
                      alt={`Thumbnail ${index + 1}`}
                      className={`w-full h-full object-cover transition-opacity duration-200 ${
                        thumbnailsLoading[index] ? "opacity-0" : "opacity-100"
                      }`}
                      onLoad={() =>
                        setThumbnailsLoading((prev) => ({
                          ...prev,
                          [index]: false,
                        }))
                      }
                      onLoadStart={() =>
                        setThumbnailsLoading((prev) => ({
                          ...prev,
                          [index]: true,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column - Details */}
        <div>
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-3 leading-tight font-fraunces">
              {listingData.basic.title}
            </h1>

            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <span className="text-purple-700 text-sm font-semibold font-afacad">
                {categoryConfig.name}
              </span>
            </div>

            {/* Price */}
            <div className="mb-4">
              <span className="text-3xl font-bold text-purple-700 font-fraunces">
                {formatPrice(listingData.basic.price)}
              </span>
            </div>

            {/* Preowned Status */}
            {renderPreownedStatus()}
          </div>

          {/* Dynamic Property Details */}
          {renderPropertyDetails()}

          {/* Dynamic Amenities */}
          {renderAmenities()}

          {/* Collapsible Sections */}
          <div className="space-y-4">
            {renderCollapsibleSection(
              "reason",
              "Reason for Selling",
              listingData.basic.story,
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 8V12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            )}

            {renderCollapsibleSection(
              "description",
              "More Description",
              listingData.basic.description,
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <polyline
                  points="14,2 14,8 20,8"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="16"
                  y1="13"
                  x2="8"
                  y2="13"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="16"
                  y1="17"
                  x2="8"
                  y2="17"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <polyline
                  points="10,9 9,9 8,9"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            )}
          </div>

          {/* Contact Section */}
          {renderContactSection()}
        </div>
      </div>
    </div>
  );
}

export default ClassifiedDetailsView;
